import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Button,
  Badge,
  useToast,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Text,
  HStack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import PendingAgents from '../components/admin/PendingAgents';
import { FiCheck, FiX } from 'react-icons/fi';
import config from '../config';
import api from '../utils/api';
import { useAuth, authApi } from '../hooks/useAuth';

const AdminDashboard = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selectedLead, setSelectedLead] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useAuth();
  const [agentHierarchy, setAgentHierarchy] = useState([]);

  const { data: leads, isError: leadsError } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      try {
        const response = await api.get('/api/leads/all');
        return response.data.data;
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch leads',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        throw error;
      }
    }
  });

  const { data: agents, isError: agentsError } = useQuery({
    queryKey: ['agents'],
    queryFn: async () => {
      try {
        const response = await api.get('/api/users/agents', {
          params: {
            populate: 'parentAgent'
          }
        });
        return response.data.data;
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch agents',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        throw error;
      }
    }
  });

  const { data: payouts, isError: payoutsError } = useQuery({
    queryKey: ['payouts'],
    queryFn: async () => {
      try {
        const response = await api.get('/api/payouts/all');
        return response.data.data;
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch payouts',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        throw error;
      }
    }
  });

  const approveAgent = useMutation({
    mutationFn: async ({ id, isApproved }) => {
      await axios.put(`${config.API_BASE_URL}/api/users/${id}/approve`, { isApproved });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast({
        title: 'Success',
        description: 'Agent status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  });

  const updateLeadStatus = useMutation({
    mutationFn: async ({ id, status }) => {
      await axios.put(`${config.API_BASE_URL}/api/leads/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({
        title: 'Success',
        description: 'Lead status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  });

  // Calculate statistics
  const totalLeads = leads?.length || 0;
  const approvedLeads = leads?.filter(lead => lead.status === 'approved').length || 0;
  const pendingLeads = leads?.filter(lead => lead.status === 'pending').length || 0;
  const totalAgents = agents?.length || 0;
  const totalPayout = payouts?.reduce((sum, payout) => sum + payout.totalAmount, 0) || 0;

  // Prepare chart data
  const chartData = payouts?.map(payout => ({
    month: new Date(payout.month).toLocaleDateString('en-US', { month: 'short' }),
    amount: payout.totalAmount,
  })) || [];

  const handleLeadAction = async (leadId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${config.API_BASE_URL}/api/leads/${leadId}/${action}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      toast({
        title: 'Success',
        description: `Lead ${action}d successfully!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || `Failed to ${action} lead`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAgentAction = async (agentId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${config.API_BASE_URL}/api/users/agents/${agentId}/${action}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      toast({
        title: 'Success',
        description: `Agent ${action}d successfully!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || `Failed to ${action} agent`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewLead = async (leadId) => {
    try {
      console.log('Attempting to view lead:', leadId);
      
      // Validate lead ID format
      if (!leadId.match(/^[0-9a-fA-F]{24}$/)) {
        throw new Error('Invalid lead ID format');
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${config.API_BASE_URL}/api/leads/${leadId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Lead details response:', response.data);
      
      if (response.data.success) {
        setSelectedLead(response.data.data);
        onOpen();
      } else {
        throw new Error(response.data.message || 'Failed to fetch lead details');
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to fetch lead details',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleLeadStatusUpdate = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${config.API_BASE_URL}/api/leads/${leadId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: `Lead ${newStatus} successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Refresh the leads data
        queryClient.invalidateQueries(['leads']);
        onClose(); // Close the modal
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || `Failed to ${newStatus} lead`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchAgentHierarchy = async () => {
    try {
      console.log('Fetching agent hierarchy...');
      const response = await authApi.get('/api/auth/agent-hierarchy');
      console.log('Agent hierarchy response:', response.data);
      setAgentHierarchy(response.data.data);
    } catch (error) {
      console.error('Error fetching agent hierarchy:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch agent hierarchy',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAgentHierarchy();
    }
  }, [user]);

  // Add error states to the UI
  if (leadsError || agentsError || payoutsError) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          There was an error loading the dashboard data. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Heading mb={6}>Admin Dashboard</Heading>

      <StatGroup mb={8}>
        <Stat>
          <StatLabel>Total Leads</StatLabel>
          <StatNumber>{totalLeads}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Approved Leads</StatLabel>
          <StatNumber>{approvedLeads}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Pending Leads</StatLabel>
          <StatNumber>{pendingLeads}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Total Agents</StatLabel>
          <StatNumber>{totalAgents}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Total Payouts</StatLabel>
          <StatNumber>₹{totalPayout.toLocaleString()}</StatNumber>
        </Stat>
      </StatGroup>

      <Box mb={8} h="300px">
        <Heading size="md" mb={4}>Monthly Payouts</Heading>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="amount" fill="#3182CE" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Tabs>
        <TabList>
          <Tab>Leads</Tab>
          <Tab>Agents</Tab>
          <Tab>Payouts</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Box overflowX="auto">
              <Table>
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Customer</Th>
                    <Th>Agent</Th>
                    <Th>Type</Th>
                    <Th>Amount</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leads?.map((lead) => (
                    <Tr key={lead._id}>
                      <Td>{new Date(lead.createdAt).toLocaleDateString()}</Td>
                      <Td>{lead.customerName}</Td>
                      <Td>{lead.agent?.name}</Td>
                      <Td>{lead.investmentType}</Td>
                      <Td>₹{lead.investmentAmount.toLocaleString()}</Td>
                      <Td>
                        <Badge
                          colorScheme={
                            lead.status === 'approved'
                              ? 'green'
                              : lead.status === 'rejected'
                              ? 'red'
                              : 'yellow'
                          }
                        >
                          {lead.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Button size="sm" onClick={() => handleViewLead(lead._id)}>
                          View Details
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>

          <TabPanel>
            <Box overflowX="auto">
              <Table>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Agent ID</Th>
                    <Th>Email</Th>
                    <Th>Phone</Th>
                    <Th>Role</Th>
                    <Th>Parent Agent</Th>
                    <Th>Status</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {agents?.map((agent) => (
                    <Tr key={agent._id}>
                      <Td>{agent.name}</Td>
                      <Td>
                        <Badge colorScheme="purple">
                          {agent.agentId || 'N/A'}
                        </Badge>
                      </Td>
                      <Td>{agent.email}</Td>
                      <Td>{agent.phone}</Td>
                      <Td>
                        <Badge colorScheme={agent.role === 'agent' ? 'blue' : 'orange'}>
                          {agent.role}
                        </Badge>
                      </Td>
                      <Td>
                        {agent.parentAgent ? (
                          <Text fontSize="sm">
                            {agent.parentAgent.name} 
                            <Badge ml={2} colorScheme="gray">
                              {agent.parentAgent.agentId}
                            </Badge>
                          </Text>
                        ) : (
                          '-'
                        )}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={agent.isApproved ? 'green' : 'red'}
                        >
                          {agent.isApproved ? 'Approved' : 'Pending'}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          colorScheme={agent.isApproved ? 'red' : 'green'}
                          onClick={() =>
                            approveAgent.mutate({
                              id: agent._id,
                              isApproved: !agent.isApproved,
                            })
                          }
                        >
                          {agent.isApproved ? 'Revoke' : 'Approve'}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>

          <TabPanel>
            <Box overflowX="auto">
              <Table>
                <Thead>
                  <Tr>
                    <Th>Agent</Th>
                    <Th>Month</Th>
                    <Th>Total Leads</Th>
                    <Th>Approved Leads</Th>
                    <Th>Amount</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payouts?.map((payout) => (
                    <Tr key={payout._id}>
                      <Td>{payout.agent.name}</Td>
                      <Td>
                        {new Date(payout.month).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Td>
                      <Td>{payout.totalLeads}</Td>
                      <Td>{payout.approvedLeads}</Td>
                      <Td>₹{payout.totalAmount.toLocaleString()}</Td>
                      <Td>
                        <Badge
                          colorScheme={payout.status === 'paid' ? 'green' : 'yellow'}
                        >
                          {payout.status}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <PendingAgents />

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Lead Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLead && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold">Customer Information</Text>
                  <Text>Name: {selectedLead.customerName}</Text>
                  <Text>Phone: {selectedLead.customerPhone}</Text>
                  <Text>Email: {selectedLead.customerEmail}</Text>
                  <Text>PAN: {selectedLead.customerPAN}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Investment Details</Text>
                  <Text>Type: {selectedLead.investmentType}</Text>
                  <Text>Amount: ₹{selectedLead.investmentAmount.toLocaleString()}</Text>
                  <Text>Scheme: {selectedLead.scheme}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Agent Information</Text>
                  <Text>Name: {selectedLead.agent?.name}</Text>
                  <Text>Agent ID: {selectedLead.agent?.agentId}</Text>
                  <Text>Phone: {selectedLead.agent?.phone}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Status Information</Text>
                  <HStack>
                    <Text>Current Status:</Text>
                    <Badge
                      colorScheme={
                        selectedLead.status === 'approved'
                          ? 'green'
                          : selectedLead.status === 'rejected'
                          ? 'red'
                          : 'yellow'
                      }
                    >
                      {selectedLead.status}
                    </Badge>
                  </HStack>
                  <Text>Submitted: {new Date(selectedLead.createdAt).toLocaleString()}</Text>
                </Box>
                {selectedLead.status === 'pending' && (
                  <HStack spacing={4} pt={4}>
                    <Button
                      leftIcon={<FiCheck />}
                      colorScheme="green"
                      onClick={() => handleLeadStatusUpdate(selectedLead._id, 'approved')}
                    >
                      Approve Lead
                    </Button>
                    <Button
                      leftIcon={<FiX />}
                      colorScheme="red"
                      onClick={() => handleLeadStatusUpdate(selectedLead._id, 'rejected')}
                    >
                      Reject Lead
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {agentHierarchy.map(agent => (
        <Box key={agent._id} p={4} borderWidth="1px" borderRadius="lg">
          <Heading size="md">{agent.name} ({agent.agentId})</Heading>
          {agent.subAgents?.length > 0 && (
            <Box ml={6} mt={2}>
              <Text fontWeight="bold">Sub Agents:</Text>
              {agent.subAgents.map(subAgent => (
                <Box key={subAgent._id} p={2}>
                  <Text>{subAgent.name} ({subAgent.agentId})</Text>
                  <Text fontSize="sm" color="gray.500">
                    Status: {subAgent.isApproved ? 'Approved' : 'Pending'}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Container>
  );
};

export default AdminDashboard;
